import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material';
import { RefreshCw as RefreshIcon } from 'lucide-react';
import { Button as UiButton, Chip, ShellHeader, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { InlineErrorPanel, LoadingSkeleton, PageContainer } from '@coordio/ui/layout';
import { memoryModuleService } from '../services/memoryModuleService';
import type { MemoryCandidateDto } from '../types/memoryModule';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';

/**
 * Memory Quarantine (Admin only, Memory spec §6A.3): candidates whose Pass 3 anonymization
 * verification failed, held with their ORIGINAL unredacted text so an Agent Admin can judge them.
 * Discard ends the candidate; Correct submits replacement text that re-runs all three passes.
 */
export const MemoryQuarantinePage = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState<MemoryCandidateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [correcting, setCorrecting] = useState<MemoryCandidateDto | null>(null);
  const [correctedText, setCorrectedText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await memoryModuleService.listQuarantine());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load the quarantine queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id: number, action: 'discard' | 'correct', text?: string) => {
    setBusyId(id);
    try {
      await memoryModuleService.resolveQuarantine(id, action, text);
      setToast(action === 'discard' ? 'Candidate discarded.' : 'Corrected text submitted — it re-runs verification.');
      setCorrecting(null);
      setCorrectedText('');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${action} the candidate`);
    } finally {
      setBusyId(null);
    }
  };

  const columns: DataTableColumn<MemoryCandidateDto>[] = [
    {
      id: 'memoryCandidateId',
      label: '#',
      minWidth: 60,
      align: 'right',
      render: (c) => c.memoryCandidateId,
    },
    {
      id: 'content',
      label: 'Original content (unredacted)',
      minWidth: 380,
      render: (c) => (
        <p className="text-sm whitespace-pre-line">{c.content}</p>
      ),
    },
    {
      id: 'failures',
      label: 'Failed checks',
      minWidth: 160,
      render: (c) => (
        <div className="flex gap-0.5 flex-wrap">
          {(c.verificationFailures ?? '').split(',').filter(Boolean).map((name) => (
            <Chip key={name} variant="outlined" label={name.trim()} />
          ))}
        </div>
      ),
    },
    {
      id: 'session',
      label: 'Session',
      minWidth: 120,
      render: (c) =>
        c.agentSessionId ? (
          <button className="text-sm text-[var(--color-accent-primary)] hover:underline" onClick={() => navigate(`/sessions/${c.agentSessionId}`)}>
            {c.agentSessionId.slice(0, 8)}…
          </button>
        ) : (
          '—'
        ),
    },
    {
      id: 'createdAtUtc',
      label: 'Quarantined',
      minWidth: 160,
      render: (c) => new Date(c.createdAtUtc).toLocaleString(),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 200,
      render: (c) => (
        <div className="flex gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <button
                    data-testid="memory-quarantine-discard-btn"
                    className="rounded-lg border border-red-500 px-3 py-1 text-sm text-red-500 hover:bg-red-500/10 disabled:opacity-50"
                    disabled={busyId === c.memoryCandidateId}
                    onClick={() => resolve(c.memoryCandidateId, 'discard')}
                  >
                    Discard
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Discard permanently — the content never becomes memory</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <button
                    data-testid="memory-quarantine-correct-btn"
                    className="rounded-lg border border-[var(--color-border-default)] px-3 py-1 text-sm hover:bg-[var(--color-bg-subtle)] disabled:opacity-50"
                    disabled={busyId === c.memoryCandidateId}
                    onClick={() => {
                      setCorrecting(c);
                      setCorrectedText(c.content);
                    }}
                  >
                    Correct
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Supply corrected text — it re-runs all three anonymizer passes</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ];

  return (
    <>
      <ShellHeader
        data-testid="memory-quarantine-header"
        title="Memory Quarantine"
        description="Candidates whose anonymization verification failed, held with their original text for review. This page is admin-only because that text is unredacted by design."
        actions={
          <UiButton data-testid="memory-quarantine-refresh-btn" variant="outline" disabled={loading} onClick={load}>
            <RefreshIcon /> Refresh
          </UiButton>
        }
      />

      <PageContainer data-testid="memory-quarantine-form-page">
        {error && (
          <InlineErrorPanel
            data-testid="memory-quarantine-error-panel"
            message={error}
            action={<UiButton variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</UiButton>}
          />
        )}

        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm mb-3">
          Content below may contain personal identifiers. It is quarantined precisely because the
          anonymizer could not clear it — treat this queue as PII.
        </div>

        {loading && rows.length === 0 ? (
          <LoadingSkeleton data-testid="memory-quarantine-loading" rows={6} />
        ) : (
          <DataTable
            data-testid="memory-quarantine-table"
            columns={columns}
            rows={rows}
            loading={loading}
            emptyMessage="Quarantine is empty — every extracted candidate cleared verification."
          />
        )}
      </PageContainer>

      <Dialog data-testid="memory-quarantine-correction-dialog" open={correcting !== null} onClose={() => setCorrecting(null)} maxWidth="md" fullWidth>
        <DialogTitle>Correct candidate #{correcting?.memoryCandidateId}</DialogTitle>
        <DialogContent>
          <p className="text-sm text-[var(--color-text-secondary)] mb-3">
            Rewrite the content without identifiers. The corrected text does not bypass anything —
            it goes back through all three anonymizer passes and quarantines again if a check fails.
          </p>
          <div>
            <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Corrected content</label>
            <Textarea
              autoFocus
              className="w-full"
              rows={5}
              value={correctedText}
              onChange={(e) => setCorrectedText(e.target.value)}
              data-testid="memory-quarantine-correctedContent"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <button className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-[var(--color-bg-subtle)]" data-testid="cancel-memory-quarantine-btn" onClick={() => setCorrecting(null)}>Cancel</button>
          <button
            className="rounded-lg bg-[var(--color-accent-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            data-testid="save-memory-quarantine-btn"
            disabled={!correctedText.trim() || busyId !== null}
            onClick={() => correcting && resolve(correcting.memoryCandidateId, 'correct', correctedText.trim())}
          >
            Submit for re-verification
          </button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </>
  );
};

export default MemoryQuarantinePage;
